/**
 * 事件考验的出题调度
 *
 * 规则（GDD 2.4）：
 * - 事件出现时插入一道题，题目与当前情境相关（用关卡卡池控制主题）；
 * - **每局 4–8 道**，多了就稀释了玩的部分（宁少勿多）；
 * - 答错不惩罚：只在本局收益与错题集上体现。
 *
 * 出题选择必须是确定性的（同一局同一事件出同一题），否则同一份 actions
 * 在客户端与服务端会产生不同的题目 —— 虽然题目本身不进模拟核心，
 * 但错题集与复算审计都依赖它，随机化只会带来麻烦。
 */

import type { KnowledgeCard, SimEvent } from "@chuandeng/shared";

export interface QuizPrompt {
  card: KnowledgeCard;
  /** 题池里含未审核卡时置 true，界面上要打「待审核」标记 */
  includesDraft: boolean;
  /** 触发它的事件（用于文案，如「因为商路断绝…」） */
  trigger: SimEvent;
}

export interface QuizSchedulerOptions {
  /** 本局最多出几道题 */
  maxQuestions?: number;
  /** 题池是否含 draft 卡 */
  includesDraft?: boolean;
}

const TRIGGER_KINDS: ReadonlySet<SimEvent["kind"]> = new Set(["randomEvent", "mistSpread"]);

export class QuizScheduler {
  private asked = 0;
  private cursor = 0;

  constructor(
    private readonly cards: readonly KnowledgeCard[],
    private readonly options: QuizSchedulerOptions = {},
  ) {}

  get askedCount(): number {
    return this.asked;
  }

  get limit(): number {
    return this.options.maxQuestions ?? 8;
  }

  get available(): boolean {
    return this.cards.length > 0;
  }

  /** 该事件是否应该出题 */
  shouldAsk(event: SimEvent): boolean {
    if (!this.available) return false;
    if (this.asked >= this.limit) return false;
    return TRIGGER_KINDS.has(event.kind);
  }

  /**
   * 取一道题。用事件内容做偏移，保证"同事件同题"，
   * 又不会连续重复同一张卡。
   */
  pick(event: SimEvent, turn: number): QuizPrompt | null {
    if (!this.shouldAsk(event)) return null;
    const index = (this.cursor + turn + event.message.length) % this.cards.length;
    const card = this.cards[index];
    if (!card) return null;
    this.cursor = (this.cursor + 1) % this.cards.length;
    this.asked += 1;
    return {
      card,
      includesDraft: this.options.includesDraft ?? false,
      trigger: event,
    };
  }

  /** 复习错题时用的"点名出题"（不走事件，直接指定卡） */
  promptForCard(card: KnowledgeCard, trigger: SimEvent): QuizPrompt {
    return {
      card,
      includesDraft: !(card.status === "approved" && card.reviewedBy !== null),
      trigger,
    };
  }
}

/** 把事件类型翻成孩子看得懂的触发文案 */
export function triggerLabel(event: SimEvent): string {
  switch (event.kind) {
    case "mistSpread":
      return "迷雾压境，需要一点智慧";
    case "randomEvent":
      return "路上遇到了变故";
    case "spread":
      return "火种抵达了新的地方";
    case "upgrade":
      return "知识与火种产生了共鸣";
    default:
      return "考验来了";
  }
}
